import { Transaction } from 'types/rest/assetCatalogTypes';
import {
  Condition,
  HttpGenericDsl,
  Matcher,
  MatcherDsl,
  MsgGenericDsl,
  MsgRequestDsl,
  Namespace,
  RequestDsl,
} from 'types/rest/dslTypes';
import _ from 'lodash';
import store from '../store/configureStore';
import { showToastNotification } from '../store/ui/toasts/toastsActions';
import { ToastType } from '../store/ui/toasts/toastsTypes';
import {
  allMatcherOperators,
  bodyMatchersDropdownOperators,
  transactionMatchersDropdownOperators,
} from '../views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import { RequestMatcherItem } from '../components/transactionEditor/RequestMatchersList';
import { isJsonString } from './jsonUtil';
import { isXmlString } from './xmlUtil';
import { BodyMatcherItem, XmlNamespaceItem } from '../components/transactionEditor/BodyMatchersList';
import { getKeyByValue } from './miscUtil';
import { DelayType, ResponseDelay } from '../types/rest/mockServiceTypes';

/**
 * Filters list of transactions by searchTerm.
 * @param transactionsToFilter list of transactions to be filtered
 * @param searchTerm searchterm
 */
export const clientSideFilterTransactions = (
  transactionsToFilter: Transaction[],
  searchTerm: string,
): Transaction[] => {
  if (searchTerm.length > 0) {
    const filteredTransactions = transactionsToFilter.filter((txn: Transaction) => {
      const idCheck = !!txn.id
        ? txn.id
            .toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        : false;
      const nameCheck = !!txn.name
        ? txn.name
            .toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        : false;
      const descriptionCheck = !!txn.description
        ? txn.description
            .toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        : false;

      // Check also by tags
      let tagsCheck = false;
      if (txn.tags && txn.tags.length !== 0) {
        const tags = txn.tags.filter((tag: string) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
        if (tags.length > 0) {
          tagsCheck = true;
        }
      }

      return idCheck || nameCheck || descriptionCheck || tagsCheck;
    });

    return filteredTransactions;
  } else {
    return transactionsToFilter;
  }
};

/**
 * Converts Matcher DTO from backend (MatcherDsl) to frontend representation (RequestMatcherItem).
 * @param matcher
 * @param id
 */
export const convertToRMI = (matcher: MatcherDsl, id: string): RequestMatcherItem => {
  return {
    id: id,
    name: matcher.key,
    operator: transactionMatchersDropdownOperators[matcher.matcherName],
    value: matcher.matchingValue,
    isRequired: !_.isNil(matcher.optional) ? !matcher.optional : true,
    isParsedFromUrl: false,
  };
};

/**
 * Updates signle transaction in array of transactions by its id.
 * @param transactions
 * @param transaction
 */
export const updateTransactionById = (transactions: Transaction[], transaction: Transaction) => {
  const index = transactions.findIndex(txn => txn.id === transaction.id);
  if (index !== -1) {
    transactions[index] = transaction;
  }
  return transactions;
};

/**
 * Creates blank DSL definition.
 */
export const getDefaultHttpEmptyDsl = (): HttpGenericDsl => {
  return {
    type: 'HTTP',
    requestDsl: {
      method: 'GET',
      host: '',
      path: '',
      url: {
        key: 'url',
        matcherName: Matcher.equals_url,
        matchingValue: '',
      },
      headers: [],
      queryParams: [],
      cookies: [],
    },
    responseDsl: {
      binary: false,
      status: 200,
      statusMessage: 'OK',
      proxyUrl: '',
      headers: [],
      contentType: 'text',
      content: '',
    },
  };
};

export const getDefaultMessagingEmptyDsl = (): MsgGenericDsl => {
  return {
    type: 'MESSAGING',
    requestDsl: {
      headers: [],
      properties: [],
      body: [],
    },
    responseDsl: {
      messageType: 'TEXT_MESSAGE',
      contentType: 'text',
      headers: [],
      content: '',
      binary: false,
      charset: 'UTF-8',
      responseDelay: { type: DelayType.NONE } as ResponseDelay,
      failoverEnabled: false,
    },
  };
};

export const containsUnsupportedMatcherWithTemplateSyntax = (operator: string | null, value: string | null) => {
  const supportedOperators = [
    'equals',
    'equals (case insensitive)',
    'is absent',
    'equals to JSON',
    'equals to XML',
    'matches XPath',
    'matches XML CDATA XPath',
    'matches JSON Path',
  ];
  const isSupportedOperator = supportedOperators.findIndex(item => item === operator) !== -1;

  // We skip the operator, if it is fully supported
  if (_.isNil(value) || _.isNil(operator) || isSupportedOperator) {
    return false;
  }

  return containsTemplateSyntax(value);
};

const templateRegexp = new RegExp(/\${[a-zA-Z0-9,']+\}/);

/**
 * Checks if provided value contains template syntax - ${}
 * @param value
 */
export const containsTemplateSyntax = (value: string) => {
  return templateRegexp.test(value);
};

/**
 * Iterates through list of matchers and checks if there is a combination of unsupported
 * matcher and template syntax present.
 * @param matchersToValidate
 */
export const isUnsupportedDataDrivenMatch = (matchersToValidate: MatcherDsl[]) => {
  let containsDataSyntaxInUnsupportedMatcher = false;
  _.forEach(matchersToValidate, matcher => {
    // Body matcher does not have key set
    const isBodyMatcher = matcher.key === '' || _.isNil(matcher.key);

    // If we are checking body matcher, the matching value needs to be decoded from base64
    const matchingValue = isBodyMatcher
      ? Buffer.from(matcher.matchingValue, 'base64').toString()
      : matcher.matchingValue;

    // Matcher from backend needs to be transformed to UI msg
    const mergedOperators = _.assign(bodyMatchersDropdownOperators, transactionMatchersDropdownOperators);
    const matcherName = mergedOperators[matcher.matcherName];

    if (containsUnsupportedMatcherWithTemplateSyntax(matcherName, matchingValue)) {
      containsDataSyntaxInUnsupportedMatcher = true;
      return false; // Finish the whole loop early (https://lodash.com/docs/4.17.15#forEach)
    }
  });
  return containsDataSyntaxInUnsupportedMatcher;
};

/**
 * Post-save validation checking if there is any request matcher containing
 * blaze data template syntax.
 */
export const postValidateTransactionForBlazeData = (transaction: Transaction) => {
  const requestDslNative: RequestDsl | MsgRequestDsl = transaction.dsl.requestDsl;
  let matchersToValidate: any[] = [];
  if (transaction.type === 'HTTP') {
    const requestDsl = requestDslNative as RequestDsl;
    matchersToValidate = _.concat(
      requestDsl.headers ? requestDsl.headers : [],
      requestDsl.body ? requestDsl.body : [],
      requestDsl.cookies ? requestDsl.cookies : [],
      requestDsl.queryParams ? requestDsl.queryParams : [],
    );
  } else {
    const requestDsl = requestDslNative as MsgRequestDsl;
    matchersToValidate = requestDsl.body;
  }

  if (isUnsupportedDataDrivenMatch(matchersToValidate)) {
    store.dispatch(
      showToastNotification(ToastType.Warning, 'Transaction was saved with unsupported data-driven match.'),
    );
  }
};

export const convertSampleBodyFromBase64 = (sampleBody: any) => {
  try {
    const decodedBody = atob(sampleBody);
    if (isJsonString(decodedBody)) {
      return { json: decodedBody };
    } else if (isXmlString(decodedBody)) {
      return { xpath: decodedBody };
    } else {
      return null;
    }
  } catch (e) {
    return null;
  }
};

/** Build sample body from backend. */
export const buildInitialSampleBody = (sampleBody: any) => {
  try {
    return JSON.parse(sampleBody as string);
  } catch (e) {
    return convertSampleBodyFromBase64(sampleBody);
  }
};

/**
 * Resolves the effective sample body for one body matcher: its own value if set,
 * otherwise falls back to the legacy, read-only transaction-level sample body for
 * the given matcher-type key ('json' | 'xpath' | 'cdata_xpath').
 */
export const resolveMatcherSampleBody = (
  ownSampleBody: string | null | undefined,
  legacySampleBody: string | null | undefined,
  matcherTypeKey: string | null,
): string => {
  if (ownSampleBody != null) {
    return ownSampleBody;
  }
  if (!legacySampleBody || !matcherTypeKey) {
    return '';
  }
  const legacyParsed = buildInitialSampleBody(legacySampleBody);
  const extracted = _.get(legacyParsed, matcherTypeKey, '');
  if (extracted) {
    return extracted;
  }
  // The legacy value may be raw, unwrapped content (e.g. `TransactionUtil.getRequestBody`
  // returns a matcher's matchingValue verbatim, with no {json, xpath} wrapper) rather than
  // the wrapper shape extracted above — fall back to using it directly when its own content
  // matches the requested matcher type.
  if (matcherTypeKey === 'json' && isJsonString(legacySampleBody)) {
    return legacySampleBody;
  }
  if ((matcherTypeKey === 'xpath' || matcherTypeKey === 'cdata_xpath') && isXmlString(legacySampleBody)) {
    return legacySampleBody;
  }
  return '';
};

/**
 * Gets full URL for the transaction in the running virtual service.
 * The transction url can be either relative path or full url including hostname.
 * @param mockServiceBaseUrl
 * @param transactionUrl
 */
export const getFullTransactionUrl = (mockServiceBaseUrl: string, transactionUrl: string) => {
  if (_.isNil(mockServiceBaseUrl) || _.isNil(transactionUrl)) {
    return null;
  }

  const isAbsolute = new RegExp('^([a-z]+://|//)', 'i');

  let relativeUrl = transactionUrl;

  //If transaction url is absolute, get just the relative part
  if (isAbsolute.test(transactionUrl)) {
    relativeUrl = '/' + transactionUrl.replace(/^(?:\/\/|[^/]+)*\//, '');
  }

  //Sanitize if both url contain slash
  if (mockServiceBaseUrl.endsWith('/') && relativeUrl.startsWith('/')) {
    relativeUrl = relativeUrl.substring(1, relativeUrl.length);
  }

  return mockServiceBaseUrl + relativeUrl;
};

/**
 * Processes status code conditions before saving. Keys & values need to be base64 encoded.
 * @param conditions list of condition to be processed.
 * @param mode - encode / decode key & value fields.
 */
export const transformConditionsToBase64 = (
  conditions: Condition[],
  mode: 'base64encode' | 'base64decode',
): Condition[] => {
  const mapped = _.cloneDeep(conditions);
  return _.map(mapped, item => {
    const condition = { ...item };
    let key = condition.matcher.key;
    let matchingValue = condition.matcher.matchingValue;
    if (mode === 'base64encode') {
      key = !_.isNil(key) ? Buffer.from(key).toString('base64') : undefined;
      matchingValue = !_.isNil(matchingValue) ? Buffer.from(matchingValue).toString('base64') : undefined;
    } else {
      key = !_.isNil(key) ? Buffer.from(key, 'base64').toString() : undefined;
      matchingValue = !_.isNil(matchingValue) ? Buffer.from(matchingValue, 'base64').toString() : undefined;
    }

    condition.matcher.key = key;
    condition.matcher.matchingValue = matchingValue;

    return condition;
  });
};

const convertBodyMatcherName = (matcherName: string | null) => {
  let result;
  if (matcherName == null) {
    return '';
  } else {
    result = getKeyByValue(allMatcherOperators, matcherName);
  }
  return result == null ? matcherName : result;
};

/**
 * Builds transaction body from txn editor.
 * @param body
 */
export const buildTransactionBody = (body: BodyMatcherItem[]): MatcherDsl[] => {
  return body.map(bm => ({
    key: '',
    matcherName: convertBodyMatcherName(bm.matcher) || '',
    cdataXpath: bm.cdataXpath ? Buffer.from(bm.cdataXpath).toString('base64') : null,
    matchingValue:
      convertBodyMatcherName(bm.matcher) === 'absent'
        ? ''
        : bm.content
        ? Buffer.from(bm.content).toString('base64')
        : '',
    sampleBody: bm.sampleBody != null ? Buffer.from(bm.sampleBody).toString('base64') : null,
    optional: !_.isNil(bm.isRequired) ? !bm.isRequired : false,
    namespaces: bm.namespaces ? buildNameSpaces(bm.namespaces) : [],
  }));
};

const buildNameSpaces = (namespaces: XmlNamespaceItem[]): Namespace[] => {
  return namespaces.map(np => ({
    prefix: np.prefix ? Buffer.from(np.prefix).toString('base64') : '',
    uri: np.uri ? Buffer.from(np.uri).toString('base64') : '',
  }));
};
